/**
 * ✨ Suggest tier names — review dialog for `suggestManufacturerTierVocabulary`
 * (design memo P1b, D19–D22), in the TimelineEnrichDialog style: optional
 * hint → run → review row by row → Merge & Save.
 *
 * Add-only: a row that matches an existing entry only adds synonyms to it, and
 * a different spelling the model prefers is shown as a note, never applied.
 * Low-confidence rows are hidden by default. Merge & Save hands the ticked rows
 * to the section, which applies them in ONE transaction against a fresh read.
 */

import { useMemo, useState } from 'react';
import type { TierVocabularyEntry } from '@watchkeeper/shared-types';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import type { SuggestTierVocabResult, SuggestedTierVocabEntry } from '../../services/firebase';
import type { AcceptedTierSuggestion } from '../../utils/tierVocabulary';

interface Props {
  ownerName: string;
  entries: TierVocabularyEntry[];
  suggest: (hint?: string) => Promise<SuggestTierVocabResult>;
  onClose: () => void;
  /** Throws to keep the dialog open. */
  onApply: (accepted: AcceptedTierSuggestion[]) => Promise<void>;
}

function errorText(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return String(err);
}

export function SuggestTierNamesDialog({ ownerName, entries, suggest, onClose, onApply }: Props) {
  const [hint, setHint] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuggestTierVocabResult | null>(null);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [showLow, setShowLow] = useState(false);
  const [saving, setSaving] = useState(false);

  const byId = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await suggest(hint.trim() || undefined);
      setResult(res);
      // Pre-tick everything that is not low confidence.
      setTicked(new Set(res.tiers.map((t, i) => (t.confidence === 'low' ? -1 : i)).filter((i) => i >= 0)));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRunning(false);
    }
  };

  const visible = (result?.tiers ?? [])
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => showLow || t.confidence !== 'low');
  const lowCount = (result?.tiers ?? []).filter((t) => t.confidence === 'low').length;

  const apply = async () => {
    if (!result) return;
    const accepted: AcceptedTierSuggestion[] = result.tiers
      .filter((_, i) => ticked.has(i))
      .map((t) => ({
        canonicalName: t.canonicalName,
        akas: t.akas,
        ...(t.rank !== undefined ? { rank: t.rank } : {}),
        ...(t.description ? { description: t.description } : {}),
        ...(t.matchesExistingEntryId ? { matchesExistingEntryId: t.matchesExistingEntryId } : {}),
      }));
    if (accepted.length === 0) return;
    setSaving(true);
    try {
      await onApply(accepted);
    } catch {
      // The section toasts the cause.
    } finally {
      setSaving(false);
    }
  };

  const toggle = (i: number) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <Modal isOpen onClose={onClose} title={`✨ Suggest tier names — ${ownerName}`} size="xl" dismissOnBackdrop={false}>
      <div className="space-y-3">
        <p className="text-xs text-gray-600">
          Proposes {ownerName}&rsquo;s official generic grade names and their other spellings from its own
          documentation and this catalogue&rsquo;s tier names. It only adds: existing names are never renamed,
          removed or re-ranked. Brand tiers are never sent or suggested.
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hint (optional)</label>
          <textarea
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            rows={2}
            placeholder="e.g. Sellita's spec sheets call the top grade Premium."
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void run()} loading={running} disabled={running || saving}>
            {result ? 'Run again' : 'Run'}
          </Button>
          {running && <span className="text-xs text-gray-500">Researching — this can take a minute…</span>}
        </div>
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}

        {result && (
          <>
            {!result.publishesGradeLadder && (
              <p className="text-sm text-gray-700 bg-gray-50 border rounded p-2">
                The model found no published grade ladder for {ownerName}. That is a valid answer — nothing to add.
              </p>
            )}
            {result.tiers.length > 0 && (
              <div className="border rounded divide-y">
                {visible.map(({ t, i }) => (
                  <SuggestionRow key={i} row={t} existing={t.matchesExistingEntryId ? byId.get(t.matchesExistingEntryId) : undefined} checked={ticked.has(i)} onToggle={() => toggle(i)} />
                ))}
                {visible.length === 0 && <p className="p-2 text-xs text-gray-500">Only low-confidence rows were returned.</p>}
              </div>
            )}
            {lowCount > 0 && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={showLow} onChange={(e) => setShowLow(e.target.checked)} />
                Show {lowCount} low-confidence row{lowCount === 1 ? '' : 's'}
              </label>
            )}
            {result.notes && (
              <div className="text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded p-2 whitespace-pre-wrap">
                <span className="font-medium">Notes from the model: </span>
                {result.notes}
              </div>
            )}
            <div className="text-[11px] text-gray-500 space-y-0.5">
              {result.uncoveredSpellings.length > 0 && (
                <p>
                  Catalogue spellings nothing covers yet: <span className="text-gray-700">{result.uncoveredSpellings.join(', ')}</span>
                </p>
              )}
              {result.droppedCount > 0 && (
                <p>{result.droppedCount} row{result.droppedCount === 1 ? '' : 's'} dropped by the check (suffix-like, no evidence, duplicates, or nothing new).</p>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void apply()} disabled={!result || ticked.size === 0 || saving} loading={saving}>
            Merge &amp; Save ({ticked.size})
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function SuggestionRow({
  row,
  existing,
  checked,
  onToggle,
}: {
  row: SuggestedTierVocabEntry;
  existing?: TierVocabularyEntry;
  checked: boolean;
  onToggle: () => void;
}) {
  const variant = row.confidence === 'high' ? 'success' : row.confidence === 'medium' ? 'warning' : 'default';
  return (
    <label className="flex gap-3 p-2 text-sm cursor-pointer hover:bg-gray-50">
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{row.canonicalName}</span>
          {existing ? (
            <Badge variant="info">adds {row.akas.length} spelling{row.akas.length === 1 ? '' : 's'} to existing</Badge>
          ) : (
            <Badge variant="info">new grade{row.rank !== undefined ? ` · rank ${row.rank}` : ''}</Badge>
          )}
          <Badge variant={variant}>{row.confidence}</Badge>
        </div>
        {row.akas.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {row.akas.map((a) => (
              <span key={a} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                {a}
              </span>
            ))}
          </div>
        )}
        {row.preferredSpelling && (
          <p className="text-xs text-amber-800">
            The model prefers the spelling &ldquo;{row.preferredSpelling}&rdquo; — note only; the existing name is kept.
          </p>
        )}
        {row.description && <p className="text-xs text-gray-600">{row.description}</p>}
        <p className="text-[11px] text-gray-500">
          {row.covers.length > 0 && <>Covers: {row.covers.join(', ')}. </>}
          {row.evidence.sourceUrls.map((u) => (
            <a key={u} href={u} target="_blank" rel="noreferrer" className="mr-2 text-brand-600 hover:underline" onClick={(e) => e.stopPropagation()}>
              {(() => {
                try {
                  return new URL(u).hostname;
                } catch {
                  return u;
                }
              })()}
            </a>
          ))}
        </p>
      </div>
    </label>
  );
}
